import { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  Check,
  Info,
  MessageSquareText,
  X
} from "lucide-react";

import { registerVexaDialogHandler } from "./dialogService";
import "./vexa-dialog.css";

function inferDialogTone(request) {
  if (request.tone) return request.tone;

  return /eliminar|borrar|desconectar|desactivar|definitiv/i.test(request.message)
    ? "danger"
    : "info";
}

export default function VexaDialogHost() {
  const [queue, setQueue] = useState([]);
  const inputRef = useRef(null);
  const current = queue[0] || null;
  const tone = current ? inferDialogTone(current) : "info";

  const enqueue = useCallback(request => {
    setQueue(previous => [...previous, request]);
  }, []);

  useEffect(() => registerVexaDialogHandler(enqueue), [enqueue]);

  useEffect(() => {
    if (!current) return undefined;

    if (current.kind === "prompt") {
      inputRef.current?.focus();
    }

    const onKeyDown = event => {
      if (event.key === "Escape") {
        event.preventDefault();
        finish(current.kind === "prompt" ? null : false);
      }

      if (event.key === "Enter") {
        event.preventDefault();
        if (event.target?.closest?.(".vexa-dialog__button--quiet")) {
          finish(current.kind === "prompt" ? null : false);
          return;
        }
        finish(
          current.kind === "prompt"
            ? inputRef.current?.value ?? ""
            : true
        );
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // The active request changes as the queue advances.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  const finish = value => {
    const active = queue[0];
    if (!active) return;

    active.resolve(value);
    setQueue(previous => previous.slice(1));
  };

  if (!current) return null;

  const isPrompt = current.kind === "prompt";
  const isDanger = tone === "danger";
  const isWarning = tone === "warning";
  const Icon = isPrompt
    ? MessageSquareText
    : isDanger || isWarning
      ? AlertTriangle
      : Info;

  return (
    <div
      className="vexa-dialog-backdrop"
      onMouseDown={event => {
        if (event.target === event.currentTarget) {
          finish(isPrompt ? null : false);
        }
      }}
    >
      <section
        aria-labelledby="vexa-dialog-title"
        aria-modal="true"
        className={`vexa-dialog vexa-dialog--${tone}`}
        role="alertdialog"
      >
        <button
          aria-label="Cerrar diálogo"
          className="vexa-dialog__close"
          onClick={() => finish(isPrompt ? null : false)}
          type="button"
        >
          <X size={18} />
        </button>

        <div className="vexa-dialog__icon">
          <Icon size={23} strokeWidth={2.1} />
        </div>

        <div className="vexa-dialog__content">
          <p className="vexa-dialog__eyebrow">VexaDent</p>
          <h2 id="vexa-dialog-title">
            {current.title || (isPrompt ? "Nueva conversación" : "Confirmar acción")}
          </h2>
          <p className="vexa-dialog__message">{current.message}</p>

          {isPrompt && (
            <input
              autoComplete="tel"
              className="vexa-dialog__input"
              defaultValue={current.defaultValue || ""}
              inputMode={current.inputMode || "text"}
              placeholder={current.placeholder || "Escribe aquí"}
              ref={inputRef}
              type={current.inputType || "text"}
            />
          )}

          <div className="vexa-dialog__actions">
            <button
              className="vexa-dialog__button vexa-dialog__button--quiet"
              onClick={() => finish(isPrompt ? null : false)}
              type="button"
            >
              {current.cancelText || "Cancelar"}
            </button>
            <button
              className={`vexa-dialog__button vexa-dialog__button--${tone}`}
              onClick={() => finish(isPrompt ? inputRef.current?.value ?? "" : true)}
              type="button"
            >
              {isPrompt ? <MessageSquareText size={16} /> : <Check size={16} />}
              {current.confirmText || (isPrompt ? "Continuar" : isDanger ? "Sí, continuar" : "Confirmar")}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
